import axios from "axios"
import type { HttpAdapter } from "../../core/adapters/axios.adapter"
import type { AttendeesResult, EventAttendeesResponse, IntroRequest, IntroResult } from "../models/attendee.models"

export class EventAttendeesService {
  constructor(private httpAdapter: HttpAdapter) {}

  async getAttendees(eventSlug: string): Promise<AttendeesResult> {
    try {
      const url = `events/${encodeURIComponent(eventSlug)}/attendees`
      const response = await this.httpAdapter.get<EventAttendeesResponse>(url)
      return { status: "ok", attendees: response.data.attendees ?? [] }
    } catch (error) {
      // 403 = authenticated but not an approved guest of this event.
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return { status: "forbidden" }
      }
      console.error("[EventAttendees] fetch failed", error)
      return { status: "error" }
    }
  }

  /** Absolute URL to the CSV download endpoint (auth header is added by the
   *  axios adapter when fetched; for a direct <a download> the browser won't
   *  send the Bearer token, so the component fetches the blob via the adapter). */
  csvPath(eventSlug: string): string {
    return `events/${encodeURIComponent(eventSlug)}/attendees.csv`
  }

  /** POST /intro-requests: the TBW team gets the request by email and in the CRM
   *  back-office, and makes the introduction by hand. `lang` localizes the in-app
   *  confirmation notification the backend creates for the requester. */
  async requestIntro(request: IntroRequest, lang: string): Promise<IntroResult> {
    try {
      await this.httpAdapter.post(`intro-requests?lang=${encodeURIComponent(lang)}`, request)
      return "sent"
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status === 403) return "forbidden"
      if (status === 429) return "rate_limited"
      console.error("[EventAttendees] intro request failed", error)
      return "error"
    }
  }

  async getAttendeesCsvBlob(eventSlug: string): Promise<Blob | null> {
    try {
      const response = await this.httpAdapter.get(this.csvPath(eventSlug), { responseType: "blob" })
      if (response.status < 200 || response.status >= 300) return null
      return response.data as Blob
    } catch (error) {
      console.error("[EventAttendees] CSV fetch failed", error)
      return null
    }
  }
}
